import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { isCollectorEnabled } from "../../../helpers/collectorSession";
import { isStudioLoggedIn } from "../../../helpers/studioSignupSession";
import "./site.scss";

export const SUPPORT_WHATSAPP = "https://wa.link/5edo3n";

export const LogoMark = () => (
  <span className="s-mark" aria-hidden="true">
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path d="M3 2v14M9 2v14M15 2v14M1 6h16M1 12h16" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="9" cy="9" r="2.4" fill="#5ac8fa" />
    </svg>
  </span>
);

const Logo = () => (
  <Link className="s-logo" to="/home" aria-label="HastKala Pramanak home">
    <LogoMark />
    <span>
      HastKala Pramanak
      <small>Proof of craft</small>
    </span>
  </Link>
);

const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const studioIn = isStudioLoggedIn();
  const links = (
    <>
      <NavLink to="/studio" activeClassName="is-on" onClick={() => setOpen(false)}>For makers</NavLink>
      {isCollectorEnabled() && (
        <NavLink to="/collectors" activeClassName="is-on" onClick={() => setOpen(false)}>For collectors</NavLink>
      )}
      <a href="/home#verify" onClick={() => setOpen(false)}>Verify</a>
      <NavLink to="/pricing" activeClassName="is-on" onClick={() => setOpen(false)}>Pricing</NavLink>
      <a href="/home#contact" onClick={() => setOpen(false)}>Contact</a>
    </>
  );
  const cta = (
    <Link className="s-btn s-btn--primary" to={studioIn ? "/dashboard" : "/studio/signup"}>
      {studioIn ? "Open Studio" : "Start free studio"}
    </Link>
  );

  return (
    <header className="s-nav">
      <div className="s-wrap s-nav__bar">
        <Logo />
        <nav className="s-nav__links" aria-label="Main">{links}</nav>
        <div className="s-nav__right">
          {!studioIn && <Link className="s-nav__login" to="/login">Log in</Link>}
          {cta}
        </div>
        <button
          type="button"
          className="s-nav__menu"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            {open ? (
              <path d="M4 4l10 10M14 4L4 14" stroke="#0b0b10" strokeWidth="1.8" strokeLinecap="round" />
            ) : (
              <path d="M2 5h14M2 9h14M2 13h14" stroke="#0b0b10" strokeWidth="1.8" strokeLinecap="round" />
            )}
          </svg>
        </button>
      </div>
      {open && (
        <div className="s-sheet">
          <div className="s-wrap">
            {links}
            {!studioIn && <Link to="/login" onClick={() => setOpen(false)}>Studio log in</Link>}
            {isCollectorEnabled() && (
              <Link to="/my-collection/login" onClick={() => setOpen(false)}>My Collection log in</Link>
            )}
            {cta}
          </div>
        </div>
      )}
    </header>
  );
};

const SiteFooter = () => (
  <footer className="s-footer" id="contact">
    <div className="s-wrap">
      <div className="s-footer__grid">
        <div>
          <Logo />
          <p className="s-footer__about">
            Supporting the makers, protecting the buyers. Product records, certificates and tags for handmade work.
          </p>
        </div>
        <div>
          <h5>Platform</h5>
          <ul>
            <li><Link to="/studio">Studio</Link></li>
            <li><Link to="/pricing">Pricing</Link></li>
            <li><Link to="/login">Studio log in</Link></li>
          </ul>
        </div>
        <div>
          <h5>Collectors</h5>
          <ul>
            {isCollectorEnabled() && <li><Link to="/collectors">My Collection</Link></li>}
            <li><a href="/home#verify">Verify a piece</a></li>
            <li><a href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Order tags</a></li>
          </ul>
        </div>
        <div>
          <h5>Contact</h5>
          <ul>
            <li><a href="mailto:kunal@hastkalapramanak.in">kunal@hastkalapramanak.in</a></li>
            <li><a href="tel:+918935017347">+91 89350 17347</a></li>
            <li><a href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">WhatsApp support</a></li>
            <li><a href="https://www.instagram.com/hastkalapramanak/" target="_blank" rel="noreferrer">Instagram</a></li>
          </ul>
        </div>
      </div>
      <div className="s-footer__legal">
        <span>© {new Date().getFullYear()} HastKala Pramanak · HSR Layout, Bengaluru</span>
        <span>
          <Link to="/terms-and-conditions">Terms</Link> · <Link to="/privacy-policy">Privacy</Link>
        </span>
      </div>
    </div>
  </footer>
);

/** Fades sections in the first time they scroll into view. Content stays visible without JS. */
const useReveal = (root: React.RefObject<HTMLDivElement>) => {
  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    el.classList.add("is-js");
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        }),
      { threshold: 0.12 }
    );
    el.querySelectorAll(".s-reveal").forEach((node) => io.observe(node));
    return () => io.disconnect();
  }, [root]);
};

/** Phones only: a bar pinned to the bottom so Verify is one tap from anywhere. */
const VerifyDock = () => (
  <div className="s-dock">
    <div className="s-dock__bar">
      <LogoMark />
      <span className="s-dock__text">Holding a tagged piece?</span>
      <a className="s-dock__btn" href="/home#verify">Verify</a>
    </div>
  </div>
);

/** Slim header for focused flows like signup: logo and a way back, nothing to wander off to. */
type HeaderLink = { to: string; label: string };

const SiteHeaderMinimal = ({ link }: { link: HeaderLink }) => (
  <header className="s-nav s-nav--minimal">
    <div className="s-wrap s-nav__bar">
      <Logo />
      <Link className="s-nav__login s-nav__login--always" to={link.to}>{link.label}</Link>
    </div>
  </header>
);

type SiteLayoutProps = {
  title: string;
  /** Focused flows (signup, log in): slim header, no footer or Verify bar. */
  minimal?: boolean;
  /** The one link in the slim header. */
  minimalLink?: HeaderLink;
  children: React.ReactNode;
};

const SiteLayout = ({ title, minimal = false, minimalLink = { to: "/login", label: "Studio log in" }, children }: SiteLayoutProps) => {
  const root = useRef<HTMLDivElement>(null);
  useReveal(root);
  // Links like /home#verify arrive before React has rendered the section, so jump once it exists.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView();
  }, []);
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
  return (
    <div className="hkp-site" ref={root}>
      {minimal ? <SiteHeaderMinimal link={minimalLink} /> : <SiteHeader />}
      <main>{children}</main>
      {!minimal && <SiteFooter />}
      {!minimal && <VerifyDock />}
    </div>
  );
};

export default SiteLayout;
